import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { useAuth } from '../contexts/AuthContext';
import PageLayout from '../components/layout/PageLayout';
import LoadingSpinner from '../components/common/LoadingSpinner';
import SocietySectionToolbar from '../components/societies/sections/SocietySectionToolbar';
import SectionFilterSelect from '../components/societies/sections/SectionFilterSelect';
import { formatPrice, formatDate } from '../utils/helpers';
import { parseSocietyUrl, getPropertyUrl } from '../utils/urlHelpers';
import propertyService from '../services/propertyService';
import { firstImageOrPlaceholder, IMAGE_PLACEHOLDER_PATH } from '../utils/imageFallback';

const PropertiesPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  
  const { isSocietySpecific, societySlug } = parseSocietyUrl(location.pathname);
  
  const [filters, setFilters] = useState({
    search: '',
    price_min: '',
    price_max: '',
    property_type: 'all',
    sortBy: 'created_at'
  });

  const loadProperties = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params = {
        page: currentPage,
        search: filters.search || undefined,
        price_min: filters.price_min || undefined,
        price_max: filters.price_max || undefined,
        property_type: filters.property_type !== 'all' ? filters.property_type : undefined,
        ordering: filters.sortBy === 'created_at' ? '-created_at' : filters.sortBy,
        is_public: true
      };

      if (isSocietySpecific && societySlug) {
        params.society = societySlug;
      }

      const response = await propertyService.getProperties(params);
      const propertiesData = response.results || response;
      
      if (currentPage === 1) {
        setProperties(propertiesData);
      } else {
        setProperties(prev => [...prev, ...propertiesData]);
      }
      
      setTotalPages(response.total_pages || 1);
      setTotalCount(response.count || propertiesData.length);
      setHasMore(response.next ? true : false);
    } catch (err) {
      console.error('Error loading properties:', err);
      setError('Failed to load properties');
    } finally {
      setLoading(false);
    }
  }, [currentPage, filters, isSocietySpecific, societySlug]);

  useEffect(() => {
    loadProperties();
  }, [loadProperties]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  };

  const handleLoadMore = () => {
    setCurrentPage(prev => prev + 1);
  };

  const handlePropertyClick = (property) => {
    navigate(getPropertyUrl(property, societySlug));
  };

  const activeFilterCount = useMemo(
    () =>
      (filters.property_type !== 'all' ? 1 : 0) +
      (filters.price_min ? 1 : 0) +
      (filters.price_max ? 1 : 0) +
      (filters.sortBy !== 'created_at' ? 1 : 0),
    [filters.property_type, filters.price_min, filters.price_max, filters.sortBy]
  );

  const handleCreateProperty = useCallback(() => {
    if (societySlug) {
      navigate(`/societies/${societySlug}/properties/create`);
    } else {
      navigate('/properties/create');
    }
  }, [navigate, societySlug]);

  return (
    <PageLayout title="Properties - PakPoints">
      <Helmet>
        <title>Properties - PakPoints</title>
        <meta name="description" content="Browse property listings across Pakistan on PakPoints." />
        <meta name="robots" content="noindex,follow" />
      </Helmet>
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="space-y-4 sm:space-y-6 mb-6">
            <SocietySectionToolbar
              title="Properties"
              count={totalCount}
              itemLabel={{ singular: 'property', plural: 'properties' }}
              searchPlaceholder="Search properties..."
              searchValue={filters.search}
              onSearchChange={(v) => handleFilterChange('search', v)}
              activeFilterCount={activeFilterCount}
              addButton={
                isAuthenticated
                  ? { label: 'Create Property', onClick: handleCreateProperty }
                  : null
              }
            >
              <SectionFilterSelect
                label="Property Type"
                value={filters.property_type}
                onChange={(e) => handleFilterChange('property_type', e.target.value)}
              >
                <option value="all">All Types</option>
                <option value="for_sale">For Sale</option>
                <option value="for_rent">For Rent</option>
              </SectionFilterSelect>
              <div>
                <label
                  htmlFor="properties-filter-min-price"
                  className="block text-sm font-medium text-gray-700 mb-1 sm:mb-2"
                >
                  Min Price
                </label>
                <input
                  id="properties-filter-min-price"
                  type="number"
                  inputMode="numeric"
                  min={0}
                  value={filters.price_min}
                  onChange={(e) => handleFilterChange('price_min', e.target.value)}
                  placeholder="Min price"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg sm:rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm bg-white"
                />
              </div>
              <div>
                <label
                  htmlFor="properties-filter-max-price"
                  className="block text-sm font-medium text-gray-700 mb-1 sm:mb-2"
                >
                  Max Price
                </label>
                <input
                  id="properties-filter-max-price"
                  type="number"
                  inputMode="numeric"
                  min={0}
                  value={filters.price_max}
                  onChange={(e) => handleFilterChange('price_max', e.target.value)}
                  placeholder="Max price"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg sm:rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm bg-white"
                />
              </div>
              <SectionFilterSelect
                label="Sort By"
                value={filters.sortBy}
                onChange={(e) => handleFilterChange('sortBy', e.target.value)}
                id="properties-filter-sort"
              >
                <option value="created_at">Newest First</option>
                <option value="-created_at">Oldest First</option>
                <option value="price">Price: Low to High</option>
                <option value="-price">Price: High to Low</option>
                <option value="title">Title: A to Z</option>
                <option value="-title">Title: Z to A</option>
              </SectionFilterSelect>
            </SocietySectionToolbar>
          </div>

          {/* Results */}
          {!error && properties && properties.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 sm:gap-6">
              {properties.map((property, index) => (
                <React.Fragment key={property.id}>
                  <div 
                    className="bg-white rounded-lg shadow-sm border hover:shadow-md transition-shadow flex flex-col h-full cursor-pointer overflow-hidden"
                    onClick={() => handlePropertyClick(property)}
                  >
                    {/* Property Image with Title Overlay */}
                    <div className="relative">
                      <div className="w-full h-48 bg-gray-200 flex items-center justify-center relative overflow-hidden group">
                        <>
                          <img
                            src={firstImageOrPlaceholder(property.images)}
                            alt={property.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                            style={{ objectFit: 'cover' }}
                            onError={(e) => {
                              e.currentTarget.src = IMAGE_PLACEHOLDER_PATH;
                            }}
                          />
                          {/* Image Count Badge */}
                          {property.images && property.images.length > 1 && (
                            <div className="absolute top-2 right-2 bg-black bg-opacity-70 text-white px-2 py-1 rounded-full text-xs">
                              +{property.images.length - 1}
                            </div>
                          )}
                          {/* View Gallery Indicator */}
                          <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-opacity duration-200 flex items-center justify-center">
                            <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                              <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                              </svg>
                            </div>
                          </div>
                        </>
                        {/* Title Overlay */}
                        <div className="absolute inset-0 bg-black bg-opacity-40 flex items-end">
                          <div className="p-4 w-full">
                            <h3 className="text-white font-semibold text-lg line-clamp-2">
                              {property.title}
                            </h3>
                          </div>
                        </div>
                        {/* Price Badge */}
                        <div className="absolute top-2 left-2">
                          <span className="bg-blue-600 text-white px-2 py-1 text-sm font-medium rounded">
                            {formatPrice(property.price)}
                          </span>
                        </div>
                        
                        {/* Property Type Badge */}
                        <div className="absolute top-2 right-2">
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                            property.property_type === 'for_rent' 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-orange-100 text-orange-800'
                          }`}>
                            {property.property_type === 'for_rent' ? 'For Rent' : 'For Sale'}
                          </span>
                        </div>
                        {/* Public Status */}
                        {!property.is_public && (
                          <div className="absolute top-2 left-2 mt-8">
                            <span className="bg-yellow-100 text-yellow-800 px-2 py-1 text-xs rounded-full">
                              Members Only
                            </span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Property Info */}
                    <div className="p-4 flex-grow flex flex-col">
                      <div className="flex-grow">
                        <p className="text-gray-600 text-sm mb-3 line-clamp-2">
                          {property.description}
                        </p>
                        
                        <div className="flex items-center text-gray-500 text-sm mb-3">
                          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                          <span className="line-clamp-1">{property.address}</span>
                        </div>
                        
                        {/* Property Size */}
                        {property.size && property.size_unit && (
                          <div className="flex items-center text-gray-500 text-sm mb-3">
                            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                            </svg>
                            <span>{property.size} {property.size_unit}</span>
                          </div>
                        )}
                      </div>

                      {/* Property Stats */}
                      <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
                        <div className="flex items-center">
                          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                          <span>{property.views || 0} views</span>
                        </div>
                        <div className="flex items-center">
                          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <span>{formatDate(property.created_at)}</span>
                        </div>
                      </div>

                      {/* Action Button */}
                      <button className="w-full bg-blue-600 text-white py-2 px-4 rounded-md text-sm font-medium hover:bg-blue-700 transition-colors">
                        View Details
                      </button>
                    </div>
                  </div>
                  
                  {/* Ad between items removed to avoid content confusion */}
                </React.Fragment>
              ))}
            </div>
          ) : !loading ? (
            <div className="text-center py-12">
              <div className="text-gray-400 mb-4">
                <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No properties found</h3>
              <p className="text-gray-500">
                Try adjusting your search criteria or browse all properties.
              </p>
            </div>
          ) : null}

          {/* Load More Button */}
          {hasMore && properties && properties.length > 0 && !loading && (
            <div className="text-center mt-8">
              <button
                onClick={handleLoadMore}
                disabled={loading}
                className="bg-blue-600 text-white px-6 py-3 rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                {loading ? 'Loading...' : 'Load More Properties'}
              </button>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="text-center py-12">
              <div className="text-red-500 text-6xl mb-4">⚠️</div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Error Loading Properties</h3>
              <p className="text-gray-500 mb-4">{error}</p>
              <button
                onClick={() => {
                  setError(null);
                  loadProperties();
                }}
                className="bg-blue-600 text-white px-6 py-3 rounded-md hover:bg-blue-700 transition-colors"
              >
                Try Again
              </button>
            </div>
          )}

          {/* Loading Spinner */}
          {loading && properties.length === 0 && (
            <div className="flex justify-center py-12">
              <LoadingSpinner size="large" />
            </div>
          )}
        </div>
      </div>
    </PageLayout>
  );
};

export default PropertiesPage;
